// Matches the existing entry prompt; this is UI timing, not a protocol change.
export const LEVEL_ENTRY_WINDOW_SECONDS = 10;
export type LevelReading = {hr:number;rpe:number};

type TimedLevelOptions = {
  durationSeconds:number;
  now:()=>number;
  onRemaining:(remaining:number)=>void;
  onEntryWindow:()=>void;
  onExpired:()=>void;
  onComplete:(reading:LevelReading,immediateStop:boolean)=>void;
};

/** Synchronous guards keep duplicate UI/timer events from committing twice. */
export function createTimedLevel(options:TimedLevelOptions) {
  const deadline=options.now()+options.durationSeconds*1000;
  let reading:LevelReading|null=null,finished=false,expired=false,prompted=false;
  function complete(value:LevelReading,immediateStop:boolean) {
    if(finished) return;
    finished=true;
    options.onComplete(value,immediateStop);
  }
  function tick() {
    if(finished) return;
    const remaining=Math.max(0,Math.ceil((deadline-options.now())/1000));
    options.onRemaining(remaining);
    // A throttled timer can skip the entire final entry window.
    if(remaining<=LEVEL_ENTRY_WINDOW_SECONDS&&!prompted&&!reading) {
      prompted=true;
      options.onEntryWindow();
    }
    if(remaining===0) {
      if(!expired) {expired=true;options.onExpired();}
      if(reading) complete(reading,false);
    }
  }
  return {
    tick,
    isRunning:()=>!finished&&options.now()<deadline,
    confirm(value:LevelReading,immediateStop=false):'pending'|'completed'|'ignored' {
      if(finished) return 'ignored';
      // A safety stop still takes precedence over an earlier pending normal reading.
      if(immediateStop) {complete(value,true);return 'completed';}
      if(reading) return 'ignored';
      reading={...value};
      if(options.now()>=deadline) {tick();return 'completed';}
      return 'pending';
    },
    cancel(){finished=true;reading=null;},
  };
}
